import { getFeedDateRangeQuery } from '@warebee/shared/dataset-queries';
import { addMilliseconds, isEqual } from 'date-fns';
import { useEffect } from 'react';
import {
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { warehouseSelectedId } from '../../store/warehouse.state';
import { actualityExtraSettings } from '../store/actuality.state';
import { executeDatasetQuery } from '../store/feed.helper';
import {
  feedDatasetId,
  feedDateRange,
  feedDateRangeAtom,
  feedEffectiveDateRange,
  feedSelectedDateRange,
} from '../store/feed.state';

function useActualityTimeWatcher() {
  const datasetId = useRecoilValue(feedDatasetId);
  const warehouseId = useRecoilValue(warehouseSelectedId);
  const feedRangeLoadable = useRecoilValueLoadable(feedDateRange);
  const selectedRangeLoadable = useRecoilValueLoadable(feedEffectiveDateRange);
  const setSelectedRange = useSetRecoilState(feedSelectedDateRange);
  const setDateRangeAtom = useSetRecoilState(feedDateRangeAtom);
  const actualitySettings = useRecoilValue(actualityExtraSettings);
  const refreshInterval = actualitySettings?.refreshInterval ?? 5;

  const feedRange =
    feedRangeLoadable.state === 'hasValue'
      ? feedRangeLoadable.getValue()
      : null;

  const selectedRange =
    selectedRangeLoadable.state === 'hasValue'
      ? selectedRangeLoadable.getValue()
      : null;
  const duration =
    selectedRange?.[1]?.getTime() - selectedRange?.[0]?.getTime();
  const shouldWatch = isEqual(
    actualitySettings?.autoRefresh && selectedRange?.[1],
    feedRange?.[1],
  );

  async function fetchDateRange() {
    try {
      const compiledQuery = getFeedDateRangeQuery(datasetId).compile();
      const [result] = await executeDatasetQuery({
        warehouseId,
        compiledQuery,
        comment: '[feed] Date Range Watcher',
      });
      return [result[0].minTime, result[0].maxTime];
    } catch (ex) {
      console.error('FetchDateRange exception:', ex);
    }
  }

  useEffect(() => {
    let timeoutId;
    if (shouldWatch) {
      timeoutId = setInterval(
        async () => {
          const [from, to] = await fetchDateRange();
          if (!isEqual(to, feedRange?.[1])) {
            setDateRangeAtom([from, to]);
            setSelectedRange([addMilliseconds(to, -duration), to]);
          }
        },
        1000 * 60 * refreshInterval,
      );
    }

    return () => {
      // console.log('Cleanup: Cancelling event time watcher');
      clearTimeout(timeoutId);
    };
  }, [shouldWatch, feedRange, refreshInterval, duration, actualitySettings]);

  return null;
}

export default useActualityTimeWatcher;
